import { ReactNode } from "react"
import { FiFileText, FiPenTool, FiTag } from "react-icons/fi"
import StatusBadge from "../ui/StatusBadge"

type ApplicationCardKind = "trademark" | "design" | "patent"

export interface ApplicationCardField {
  label: string
  value: ReactNode
  /** Takes the whole row, for long values */
  wide?: boolean
}

interface ApplicationCardProps {
  kind: ApplicationCardKind
  reference: string
  /** Under the reference: the kind, and for a patent its type */
  subtitle?: string
  status?: string
  fields: ApplicationCardField[]
  /** The row of action buttons at the foot of the card */
  actions: ReactNode
  /** Notes and panels that belong to the application, below the actions */
  children?: ReactNode
}

// One application of the list: what it is at the top, the facts in the middle,
// the actions at the foot
export default function ApplicationCard({
  kind,
  reference,
  subtitle,
  status,
  fields,
  actions,
  children
}: ApplicationCardProps) {
  const hasReference = Boolean(reference) && reference !== "-"

  return (
    <div className="rounded-[14px] border border-appLine bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-appTile text-dantzBlue">
            {kind === "trademark" ? <FiTag size={20} /> : kind === "design" ? <FiPenTool size={20} /> : <FiFileText size={20} />}
          </span>
          <div className="min-w-0">
            <h3 className={`break-words text-[17px] font-semibold ${hasReference ? "text-appInk" : "text-appMuted"}`}>
              {hasReference ? reference : "No reference yet"}
            </h3>
            {subtitle && <p className="mt-0.5 text-[13px] text-appMuted">{subtitle}</p>}
          </div>
        </div>
        {status && <StatusBadge status={status} tinted />}
      </div>

      <dl className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-6 gap-y-4">
        {fields.map((field) => (
          <div key={field.label} className={field.wide ? "col-span-full" : undefined}>
            <dt className="text-xs font-medium uppercase tracking-[0.06em] text-appMuted">{field.label}</dt>
            <dd className="mt-1 break-words text-sm text-appInk">{field.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[#EEF1F4] pt-4">
        {actions}
      </div>

      {children}
    </div>
  )
}
